import { useEffect, useState } from 'react'
import { useDeletedProjects, usePlatforms, useRestoreProject } from '../api/hooks'
import type { Project } from '../api/types'

/** Deleted client projects are invisible everywhere else on purpose (see
 *  routers/editor.py's list_for_platform) — this is the one place "it can be
 *  restored" is actually a claim the user can act on, rather than something
 *  only reachable by knowing the project id and hitting the API directly. */
export function ClientProjectsTab() {
  const platforms = usePlatforms()
  const [platform, setPlatform] = useState<string>('')

  useEffect(() => {
    if (!platform && platforms.data && platforms.data.length > 0) {
      setPlatform(platforms.data[0].slug)
    }
  }, [platform, platforms.data])

  const deleted = useDeletedProjects(platform || undefined)
  const restore = useRestoreProject(platform)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-ink-600">
          Deleting a client project (from its editor's left rail) is a soft delete: the client's
          overridden wording immediately stops being served — its apps fall back to the default
          project's wording — but nothing is erased. Its translations, revisions and this list
          entry are kept, and restoring it here brings the overrides straight back.
        </p>
        <label className="shrink-0 text-xs">
          <span className="label mr-1.5">Platform</span>
          <select
            className="field inline-block w-auto"
            value={platform}
            onChange={(event) => setPlatform(event.target.value)}
          >
            {(platforms.data ?? []).map((item) => (
              <option key={item.slug} value={item.slug}>
                {item.slug}
              </option>
            ))}
          </select>
        </label>
      </div>

      {deleted.isError && (
        <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900">
          Could not load deleted projects: {(deleted.error as Error).message}
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-ink-100 text-2xs uppercase tracking-wider text-ink-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Slug</th>
              <th className="px-3 py-2 text-left font-semibold">Name</th>
              <th className="px-3 py-2 text-left font-semibold">Deleted</th>
              <th className="px-3 py-2 text-left font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {(deleted.data ?? []).map((project) => (
              <DeletedProjectRow key={project.id} project={project} onRestore={() => restore.mutate(project.id)} restoreBusy={restore.isPending} />
            ))}
            {deleted.isSuccess && deleted.data.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-ink-600">
                  {platform ? `No deleted client projects on ${platform}.` : 'No platform selected.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DeletedProjectRow({
  project,
  onRestore,
  restoreBusy,
}: {
  project: Project
  onRestore: () => void
  restoreBusy: boolean
}) {
  return (
    <tr>
      <td className="px-3 py-2 font-mono text-ink-800">{project.slug}</td>
      <td className="px-3 py-2">{project.name}</td>
      <td className="px-3 py-2 text-ink-600">
        {project.deleted_at ? new Date(project.deleted_at).toLocaleString() : '—'}
      </td>
      <td className="px-3 py-2">
        <button className="btn" disabled={restoreBusy} onClick={onRestore}>
          Restore
        </button>
      </td>
    </tr>
  )
}
